import React, { FC, ReactNode, useEffect, useState } from 'react';
import { ColumnProps, PaginationConfig } from 'antd/lib/table';
import PageWithHeader from '@app/components/PageWithHeader';
import PageHeader from '@app/components/header/PageHeader';
import TableOfCards from '@app/components/tables/TableOfCards';
import { toAntdPaginationTotal } from '@app/components/tables/paginationTotal';
import { TableImportantText, TableStandardText } from '@app/components/tables/Table';
import InputBox from '@app/components/inputs/inputBox/InputBox';
import NakedFormInput from '@app/components/inputs/NakedFormInput';
import useLoadable from '@app/hooks/loadable/useLoadable';
import useLastResolved from '@app/hooks/loadable/useLastResolved';
import useInfraStores from '@app/hooks/useInfraStores';
import { useDebounce } from '@app/hooks/useDebounce';
import Loadable from '@app/utils/Loadable';
import browserHistory from '@app/utils/browserHistory';
import { OrganizationServerResponse } from '@app/domain/userManagement/organizations';
import { searchOrganizations } from '@app/services/userManagement/organizationsSearchService';
import { Skeleton } from '@/components/ui/skeleton';
import MorteeMode from '@mortee/morteeMode';
import { LoadError } from '@mortee/routes/organizationSetup/OrganizationSetupErrors';
import validationSystemServices from '@mortee/services/validationSystemServices';
import { ValidationSystemOrganizationServerResponse } from '@mortee/domain/validationSystem';

const PAGE_SIZE_OPTIONS = ['10', '25', '50', '100'];
const SKELETON_ROW_ID_PREFIX = 'skeleton-';

interface OrganizationsTableState {
  page: number;
  pageSize: number;
}

const OrganizationSetupListPage: FC = () => {
  const { navigationStore } = useInfraStores<MorteeMode>();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const debouncedSearchTerm = useDebounce(searchTerm.trim());
  const [tableState, setTableState] = useState<OrganizationsTableState>({ page: 1, pageSize: 25 });

  useEffect(() => {
    setTableState((currentTableState) => (currentTableState.page === 1 ? currentTableState : { ...currentTableState, page: 1 }));
  }, [debouncedSearchTerm]);

  // The page shows its own errors
  const [organizationsPage] = useLoadable(
    (abortSignal) =>
      searchOrganizations(
        {
          query: debouncedSearchTerm || undefined,
          sort: 'Name',
          order: 'ASC',
          offset: (tableState.page - 1) * tableState.pageSize,
          limit: tableState.pageSize,
        },
        abortSignal,
        true,
      ),
    [debouncedSearchTerm, tableState],
  );
  const lastResolvedOrganizationsPage = useLastResolved(organizationsPage);

  // Only organizations that use supplier validation come back, with their portal's subdomain and reference id
  const [listedOrganizations] = useLoadable(() => validationSystemServices.getAllValidationSystemOrganizations(true), []);
  const listedOrganizationsById: Loadable<Map<string, ValidationSystemOrganizationServerResponse>> = listedOrganizations.map(
    (organizations) => new Map(organizations.map((organization) => [organization.id, organization])),
  );

  const paginationConfig: PaginationConfig = {
    current: tableState.page,
    pageSize: tableState.pageSize,
    total: toAntdPaginationTotal(lastResolvedOrganizationsPage?.totalElements ?? 0),
    showSizeChanger: true,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    hideOnSinglePage: false,
  };

  return (
    <PageWithHeader width='full' header={<PageHeader>Organization Setup</PageHeader>}>
      <InputBox appearance='line' colorScheme='secondary'>
        <NakedFormInput
          name='organization-setup-list-search'
          dataTestId='organization-setup-list-search'
          type='text'
          colorScheme='secondary'
          placeholderStyle='onlyWhenEmpty'
          heightType='thick'
          clearable
          placeholder='🔍 Search for Id / Name'
          value={searchTerm}
          onChange={(newValue): void => setSearchTerm(newValue ?? '')}
          autoFocus
        />
      </InputBox>
      {organizationsPage.isRejected() && (
        <LoadError
          error={organizationsPage.stateMetadata?.error}
          message='Cannot get the organizations.'
          dataTestId='organization-setup-list-error'
        />
      )}
      <TableOfCards
        id='organization-setup-list-table'
        dataTestId='organization-setup-list-table'
        columns={createColumns(listedOrganizationsById)}
        // The first load shows placeholder rows; later loads keep the previous page's rows under the spinner
        dataSource={
          lastResolvedOrganizationsPage?.content ??
          (organizationsPage.isRejected() ? [] : createSkeletonRows(tableState.pageSize))
        }
        loading={!!lastResolvedOrganizationsPage && organizationsPage.isNotSettled()}
        pagination={paginationConfig}
        onChange={(newPagination: PaginationConfig): void => {
          const pageSize = newPagination.pageSize ?? tableState.pageSize;
          setTableState({ page: pageSize === tableState.pageSize ? (newPagination.current ?? 1) : 1, pageSize });
        }}
        onRow={(organization: OrganizationServerResponse): { onClick: React.MouseEventHandler<HTMLDivElement> } => ({
          onClick: (event): void => {
            event.preventDefault();
            event.stopPropagation();
            if (!isSkeletonRow(organization)) {
              browserHistory.push(navigationStore.generateOrganizationSetupLink(organization.id));
            }
          },
        })}
        rowClickable
        rowKey={(organization: OrganizationServerResponse): string => organization.id}
      />
    </PageWithHeader>
  );
};

export default OrganizationSetupListPage;

function createSkeletonRows(count: number): OrganizationServerResponse[] {
  return Array.from({ length: Math.min(count, 10) }, (_value, index) => ({
    id: `${SKELETON_ROW_ID_PREFIX}${index}`,
    name: '',
    serviceIds: [],
  }));
}

function isSkeletonRow(organization: OrganizationServerResponse): boolean {
  return organization.id.startsWith(SKELETON_ROW_ID_PREFIX);
}

function createColumns(
  listedOrganizationsById: Loadable<Map<string, ValidationSystemOrganizationServerResponse>>,
): ColumnProps<OrganizationServerResponse>[] {
  const renderListedInfo = (
    organization: OrganizationServerResponse,
    toText: (listedOrganization: ValidationSystemOrganizationServerResponse | undefined) => string,
  ): ReactNode => {
    if (isSkeletonRow(organization) || listedOrganizationsById.isNotSettled()) {
      return <Skeleton className='h-4 w-16' />;
    }

    if (listedOrganizationsById.isRejected()) {
      return <TableStandardText className='text-muted-foreground'>Unavailable</TableStandardText>;
    }

    return <TableStandardText>{toText(listedOrganizationsById.result.get(organization.id))}</TableStandardText>;
  };

  return [
    {
      title: 'Organization',
      key: 'name',
      width: '45%',
      render: (_text: unknown, organization: OrganizationServerResponse): ReactNode =>
        isSkeletonRow(organization) ? (
          <Skeleton className='h-4 w-48' data-testid='organization-setup-list-skeleton-row' />
        ) : (
          <TableImportantText data-testid='organization-setup-list-name'>{organization.name}</TableImportantText>
        ),
    },
    {
      title: 'Uses Supplier Validation',
      key: 'usesSupplierValidation',
      width: '20%',
      render: (_text: unknown, organization: OrganizationServerResponse): ReactNode =>
        renderListedInfo(organization, (listedOrganization) => (listedOrganization ? 'Yes' : '—')),
    },
    {
      title: 'Registration Portal',
      key: 'portal',
      width: '35%',
      render: (_text: unknown, organization: OrganizationServerResponse): ReactNode =>
        renderListedInfo(organization, (listedOrganization) => listedOrganization?.subdomain ?? listedOrganization?.ref ?? '—'),
    },
  ];
}
